import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api.js";
import "../style/css/AppointmentsList.css"; // reuse existing styles from appointments list
import {
  PatientsApiResponse,
  RegisteredPatientsTable,
} from "./RegisteredPatientsTable";
import Loader from "../components/Loader";

const PatientsPage: React.FC = () => {
  const [patientsData, setPatientsData] = useState<PatientsApiResponse>({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // API base path and pagination params (only query string part is stored)
  const [basePath, setBasePath] = useState<string>("/patients/");
  const [paginationParams, setPaginationParams] = useState<string>("?page=1");

  // Build URL from path + pagination params (keeps it extensible later)
  const buildApiUrl = useCallback((path: string, pageParams: string) => {
    const params = new URLSearchParams(pageParams.replace("?", ""));
    return `${path}?${params.toString()}`;
  }, []);

  const currentUrl = useMemo(() => {
    return buildApiUrl(basePath, paginationParams);
  }, [basePath, paginationParams, buildApiUrl]);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(currentUrl);
        setPatientsData(response.data);
      } catch (error) {
        setError("Error al cargar la lista de pacientes");
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, [currentUrl]);

  const handleNextPage = () => {
    if (patientsData.next) {
      const nextQueryParams = new URL(patientsData.next).search;
      setPaginationParams(nextQueryParams);
    }
  };

  const handlePreviousPage = () => {
    if (patientsData.previous) {
      const prevQueryParams = new URL(patientsData.previous).search;
      setPaginationParams(prevQueryParams);
    }
  };

  const handleNewPatient = () => {
    navigate("/patient/new");
  };

  const handleView = (id: string) => alert(`Viewing patient: ${id}`);
  const handleEdit = (id: string) => alert(`Editing patient: ${id}`);

  // Show loader while loading
  if (loading) {
    return (
      <div
        className="container agenda-content full-width-main"
        id="main-content-patients"
      >
        <div className="main-calendar-view">
          <Loader show={true} />
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div
        className="container agenda-content full-width-main"
        id="main-content-patients"
      >
        <div className="main-calendar-view">
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <h3>Error al cargar pacientes</h3>
            <p>{error}</p>
            <button
              className="btn btn--primary"
              onClick={() => window.location.reload()}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RegisteredPatientsTable
      data={patientsData}
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      onNewPatientClick={handleNewPatient}
      onViewPatient={handleView}
      onEditPatient={handleEdit}
    />
  );
};

export default PatientsPage;
